/**
 * A set of functions that map a marketing event to its corresponding channel, platform, audience targeting and ad type.
 * Uses regular expressions and memoization to improve performance.
 *
 * @module marketingEventMapping
 */

import { AUDIENCE_TARGETING_MAPPING, CHANNEL_MAPPING, PLATFORM_MAPPING, AD_TYPE_MAPPING } from './constants';

const UNCLASSIFIED = 'unclassified';

/**
 * Generates an array of objects containing `text`, `regex`, and `value` properties
 * based on the given mapping constant. Each object in the array represents an entry
 * from the mapping constant.
 *
 * The regex for each entry is constructed based on the following logic:
 * - **`matches`**: An array of arrays where each inner array represents an OR condition
 *   within an AND group. All groups must match (AND), but within each group, only one
 *   term needs to match (OR). For example, `[['tvsci', 'scientific']]` will match
 *   strings that contain either `tvsci` or `scientific`.
 * - **`orGroups`**: An array of AND groups, where each group requires all terms to
 *   match (AND within the group). However, only one group needs to match across the entire
 *   `orGroups` array (OR across groups). For example, `[ ['tv', 'sci'], ['media', 'ad']]`
 *   will match strings containing both `tv` and `sci` or both `media` and `ad`.
 * - **`exclude`**: An array of terms to exclude from the match. The regex will use a negative
 *   lookahead to ensure none of these terms are present in the target string.
 * - **Special case**: If the key is `direct`, it is handled with a unique pattern to match either
 *   a single exact term or the term followed by an underscore and any characters.
 *
*
 * @param {object} mappingConstant - A mapping constant object where:
 *                                   - `matches` (Array, optional): An array of arrays, where each inner array represents
 *                                     terms in an "OR within AND" group to match in the regex.
 *                                   - `orGroups` (Array, optional): An array of groups where each group
 *                                     is an "AND within OR" group, requiring all terms in one group
 *                                     to match.
 *                                   - `exclude` (Array, optional): An array of terms to exclude from the match.
 *                                   - `text` (String): A descriptive label for the entry.
 * @returns {Array} An array of objects, where each object represents an option from the mapping constant
 *                  with properties:
 *                  - `text` (String): The descriptive label for the option.
 *                  - `regex` (RegExp): A regex pattern constructed based on `matches` and `exclude` criteria.
 *                  - `value` (String): The original key from the mapping constant.
 *
 * @example
 *  const CHANNEL_MAPPING = {
 *   referral: {
 *     matches: [['refer']],
 *     text: 'Referral',
 *     exclude: ['email', 'sms', 'affiliate'],
 *   },
 *   streaming: {
 *     matches: [['ctv', 'ott', 'streaming']],
 *     text: 'Streaming CTV OTT',
 *     exclude: ['audio', 'display', 'bottom', 'email', 'sms', 'influencer', 'refer', 'affiliate'],
 *   },
 *   tv_scientific: {
 *     matches: [['tvsci', 'scientific']],
 *     orGroups: [['tv', 'sci']],
 *     text: 'TV Scientific',
 *   }
 * };
 *
 * const regexOptions = createRegexOptions(CHANNEL_MAPPING);
 * console.log(regexOptions);
 * Output:
 * [
 *   { text: 'Referral', regex: /^(?!.*(email|sms|affiliate)).*(?=.*(refer)).*$/i, value: 'referral' },
 *   { text: 'Streaming CTV OTT', regex: /^(?!.*(audio|display|bottom|email|sms|influencer|refer|affiliate)).*(?=.*(ctv|ott|streaming)).*$/i, value: 'streaming' }
 * *   { text: 'TV Scientific', regex: /(?=.*(tvsci|scientific))|(?=.*tv)(?=.*sci).*$/i, value: 'tv_scientific' }
 * ]
 */
export const createRegexOptions = mappingConstant => {
  if (!Object.keys(mappingConstant).length) return [];
  const regexObj = Object.keys(mappingConstant)
    .reduce((accu, curr) => {
      const currentMapping = mappingConstant[curr];

      if (curr === 'Direct') {
        const directMatches = currentMapping.matches.join('|');
        accu[curr] = new RegExp(`^${directMatches}_*$|^${directMatches}$`, 'i');
        return accu;
      }

      // Handle `matches` as AND groups with OR conditions within each group
      const andGroups = currentMapping.matches
        ? currentMapping.matches
          .map(orGroup => `(?=.*(${orGroup.join('|')}))`) // OR within each AND group
          .join('')  // AND across groups
        : '';

      // Handle `orGroups`: each group requires an AND match within it, OR across groups
      const orGroups = currentMapping.orGroups
        ? currentMapping.orGroups
          .map(andGroup => andGroup.map(term => `(?=.*${term})`).join('')) // AND within each OR group
          .join('|') // OR across groups
        : '';

      // Exclude pattern for items in 'exclude' if present
      const excludePattern = currentMapping.exclude ? currentMapping.exclude.join('|') : '';
      let pattern = '';
      if (excludePattern.length) {
        pattern += `^(?!.*(${excludePattern})).*`;
      }
      if (andGroups.length) {
        pattern += `(${andGroups})`;
      }
      if (orGroups.length) {
        if (andGroups.length) {
          pattern += '|'; // OR between `matches` pattern and `orGroups` are both present
        }
        pattern += `(${orGroups})`;
      }
      pattern += '.*$';
      accu[curr] = new RegExp(pattern, 'i');
      return accu;
    }, {});
  return Object.keys(regexObj).map(value => ({
    text: mappingConstant[value].text,
    regex: regexObj[value],
    value,
  }));
};

const channelRegexOptions = createRegexOptions(CHANNEL_MAPPING);
const audienceTargetRegexOptions = createRegexOptions(AUDIENCE_TARGETING_MAPPING);
const adTypeRegexOptions = createRegexOptions(AD_TYPE_MAPPING);

const eventToChannel = {};
const eventToPlatform = {};
const eventToAudienceTarget = {};
const eventToAdType = {};

/**
 * Finds the channel that matches the given marketing event.
 *
 * @private
 * @param {string} marketingEvent - The marketing event.
 * @returns {string|null} - The channel corresponding to the marketing event. If no channel is found, returns 'unclassified'.
 */
const _marketingEventToChannel = marketingEvent => {
  const foundChannel = channelRegexOptions.find(({ regex }) => marketingEvent.match(regex))?.value;
  return foundChannel || UNCLASSIFIED;
};

/**
 * Finds the channel that matches the given marketing event.
 * Uses memoization to improve performance.
 *
 * @param {string} marketingEvent - The marketing event.
 * @returns {string} - The channel corresponding to the marketing event. If no channel is found, returns 'unclassified'.
 */
export const marketingEventToChannel = marketingEvent => {
  const memoized = eventToChannel[marketingEvent];
  if (memoized) return memoized;

  const result = _marketingEventToChannel(marketingEvent);
  eventToChannel[marketingEvent] = result;
  return result;
};

/**
 * Finds the platform that matches the given marketing event.
 *
 * @private
 * @param {string} marketingEvent - The marketing event.
 * @returns {string|null} - The platform corresponding to the marketing event. If no platform is found, returns 'unclassified'.
 */
const _marketingEventToPlatform = (marketingEvent, platformMappings) => {
  const { Unclassified: unclassified, ...restMappings } = platformMappings || {};
  const reorderedMappings = {
    ...restMappings,
    ...(unclassified ? { Unclassified: unclassified } : {}),
  };

  const platformRegexOptions = platformMappings ? createRegexOptions(reorderedMappings) : createRegexOptions(PLATFORM_MAPPING);
  const foundPlatform = platformRegexOptions.find(({ regex }) => marketingEvent.match(regex))?.value;
  return foundPlatform || UNCLASSIFIED;
};

/**
 * Finds the platform that matches the given marketing event.
 * Uses memoization to improve performance.
 *
 * @param {string} marketingEvent - The marketing event.
 * @returns {string} - The platform corresponding to the marketing event. If no platform is found, returns 'unclassified'.
 */
export const marketingEventToPlatform = (marketingEvent, platformMappings) => {
  const memoized = eventToPlatform[marketingEvent];
  if (memoized) return memoized;

  const result = _marketingEventToPlatform(marketingEvent, platformMappings);
  eventToPlatform[marketingEvent] = result;
  return result;
};

/**
 * Finds the ad type that matches the given marketing event.
 *
 * @private
 * @param {string} marketingEvent - The marketing event.
 * @returns {string|null} - The ad type corresponding to the marketing event. If no ad type is found, returns 'unclassified'.
 */
const _marketingEventToAdType = marketingEvent => {
  const foundAdType = adTypeRegexOptions.find(({ regex }) => marketingEvent.match(regex))?.value;
  return foundAdType || UNCLASSIFIED;
};

/**
 * Finds the ad type that matches the given marketing event.
 * Uses memoization to improve performance.
 *
 * @param {string} marketingEvent - The marketing event.
 * @returns {string} - The ad type corresponding to the marketing event. If no ad type is found, returns 'unclassified'.
 */
export const marketingEventToAdType = marketingEvent => {
  const memoized = eventToAdType[marketingEvent];
  if (memoized) return memoized;

  const result = _marketingEventToAdType(marketingEvent);
  eventToAdType[marketingEvent] = result;
  return result;
};

/**
 * Finds the audience target that matches the given marketing event.
 *
 * @private
 * @param {string} marketingEvent - The marketing event.
 * @returns {string|null} - The audience target corresponding to the marketing event. If no audience target is found, returns 'unclassified'.
 */
const _marketingEventToAudienceTarget = marketingEvent => {
  const foundAudienceTarget = audienceTargetRegexOptions.find(({ regex }) => marketingEvent.match(regex))?.value;
  return foundAudienceTarget || UNCLASSIFIED;
};

/**
 * Finds the audience target that matches the given marketing event.
 * Uses memoization to improve performance.
 *
 * @param {string} marketingEvent - The marketing event.
 * @returns {string} - The audience target corresponding to the marketing event. If no audience target is found, returns 'unclassified'.
 */
export const marketingEventToAudienceTarget = marketingEvent => {
  const memoized = eventToAudienceTarget[marketingEvent];
  if (memoized) return memoized;

  const result = _marketingEventToAudienceTarget(marketingEvent);
  eventToAudienceTarget[marketingEvent] = result;
  return result;
};
